

import { Burden } from '@deqo/foundation';
import { Attribute, Component } from '../../../concepts/objects';
import { ActiveObject, useResolve } from '../../Container';
import { BindingTarget } from './BindingTarget';

type BindingEntry = {
    target: BindingTarget<unknown>,
    burden: Burden,
};

export class BindingManager extends ActiveObject {

    private bindings = new Map<string, BindingEntry>();

    public start() {
        this.burden(() =>
            () => this.unbindAll(),
        );
    }

    public check(object: Component, key: string) {
        return this.bindings.get(object.id)?.target?.key === key;
    }

    public lookup<T>(object: Component): BindingTarget<T> {
        const entry = this.bindings.get(object.id);
        return (entry?.target ?? null) as BindingTarget<T> | null;
    }

    public bind<T, S extends Component = Component>(object: S, target: BindingTarget<T>, attr: Attribute<T, S>) {
        // Only rebind if id/type are not a match
        this.unbindIfNot(object, target);
        if (this.bindings.has(object.id)) return;

        const sync = target.observe(state => {
            if (object.alive) {
                attr.set(object, state);
            }
        });

        const clean = () => {
            if (object.alive) {
                attr.setMaybe(object, null);
            }
        };

        const burden = () => Burden.resolveAll([sync, clean]);

        // Order here matters as the attr.set could trigger a rebind
        this.bindings.set(object.id, { target, burden });
        attr.set(object, target.getState());
    }

    public unbind(object: Component) {
        const entry = this.bindings.get(object.id);
        if (!entry) return;
        Burden.resolve(entry.burden);
        this.bindings.delete(object.id);
    }

    private unbindIfNot(object: Component, target: BindingTarget<unknown>) {
        const entry = this.bindings.get(object.id);
        if (!entry) return;
        if (entry.target.key !== target.key) {
            this.unbind(object);
        }
    }

    private unbindAll() {
        for (const { burden } of this.bindings.values()) {
            try {
                Burden.resolve(burden);
            }
            catch { /* Ignore */ }
        }
        this.bindings.clear();
    }
}

export function useBinding<T>(object: Component) {
    const bindings = useResolve(BindingManager);
    return bindings.lookup<T>(object);
};
